/* eslint-disable react/display-name */
import cx from 'classnames';
import { getPaxDeliveryError } from 'common/anytime-booking/delivery';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import usePrevious from 'common/usePrevious';
import { codAdditionalServiceID } from 'constants/anytime-booking-constants';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import { Table } from 'grab-ui';
import { isSoftBlockError } from 'helper/quote';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _some from 'lodash/some';
import _sumBy from 'lodash/sumBy';
import PropTypes from 'prop-types';
import groupingDeliveriesSvg from 'public/images/grouping-deliveries.svg';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { enableDeliveryGuaranteeSelector } from 'selectors/app-selector';
import { bulkEditOverlaySelector } from 'selectors/new-booking-selectors';
import {
  bulkUploadGroupingClusterMappingsSelector,
  bulkUploadGroupingFetchingQuoteSelector,
  bulkUploadGroupingIsGroupedSelector,
} from 'selectors/new-bulk-upload-selectors';
import BulkActions from './bulk-actions';
import styles from './bulk-booking-editor.less';
import ExpandedDeliveryRender from './expanded-delivery';
import {
  AddOnsField,
  AddressField,
  DeliveryGuaranteeField,
  DeliveryOptionsField,
  NameField,
  PriceField,
  SizeAndWeightField,
  VehicleTypeField,
} from './table-fields';

const BulkBookingEditor = ({
  dispatch,
  deliveries,
  overlay,
  selectedDeliveryIDs,
  onSelectDeliveryIDs,
  bulkUploadFetchingGroupQuote,
  eligibleGroupingDeliveryIDs,
  shouldShowGroupingOverlay,
}) => {
  const [expandedDeliveryIDs, expandDeliveries] = useState([]);
  useEffect(
    () => {
      expandDeliveries(
        deliveries
          .filter(
            delivery =>
              _get(delivery, 'detail.dropOffs.length', 0) > 1 &&
              !getPaxDeliveryError(delivery),
          )
          .map(delivery => delivery.id),
      );
    },
    [expandDeliveries, deliveries],
  );

  const enableInsuranceFee = useSelector(enableDeliveryGuaranteeSelector);

  const eligibleGroupingDeliveryIDsCount = useMemo(
    () =>
      !eligibleGroupingDeliveryIDs
        ? 0
        : _sumBy(eligibleGroupingDeliveryIDs, group => group.length),
    [eligibleGroupingDeliveryIDs],
  );

  const onToggleExpandDelivery = useCallback(
    deliveryID => () => {
      if (expandedDeliveryIDs.includes(deliveryID)) {
        expandDeliveries(expandedDeliveryIDs.filter(id => id !== deliveryID));
      } else {
        expandDeliveries([...expandedDeliveryIDs, deliveryID]);
      }
    },
    [expandedDeliveryIDs, expandDeliveries],
  );

  const haveAnyAddOns = useMemo(
    () => {
      if (!deliveries || deliveries.length <= 0) {
        return false;
      }
      return _some(
        deliveries,
        delivery =>
          _get(delivery, 'config.additionalServices', []).filter(
            addOn => addOn.id !== codAdditionalServiceID,
          ).length > 0,
      );
    },
    [deliveries],
  );

  const columns = useMemo(
    () =>
      [
        {
          key: 'name',
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_name_happypath',
          ),
          render: (_, delivery) => (
            <NameField
              delivery={delivery}
              onToggleExpandDelivery={onToggleExpandDelivery}
            />
          ),
          width: '170px',
        },
        {
          key: 'address',
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_address_happypath',
          ),
          render: (_, delivery) => (
            <AddressField
              delivery={delivery}
              onToggleExpandDelivery={onToggleExpandDelivery}
            />
          ),
          width: '250px',
        },
        {
          title: 'Item',
          key: 'sizeAndWeightField',
          render: (_, delivery) => <SizeAndWeightField delivery={delivery} />,
          width: '100px',
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_weight_happypath',
          ),
          key: 'weight',
          render: (_, delivery) => (
            <div>
              {_get(delivery, 'detail.dropOffs', []).reduce(
                (partialWeight, step) =>
                  partialWeight + _get(step, 'itemWeight', 0),
                0,
              )}
              kg
            </div>
          ),
          width: '60px',
          // hide due to big parcel enabled
          hidden: true,
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_delivery_options_happypath',
          ),
          key: 'deliveryOptions',
          render: (_, delivery) => <DeliveryOptionsField delivery={delivery} />,
          width: '200px',
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_vehicle_type_happypath',
          ),
          key: 'vehicleType',
          render: (_, delivery) => <VehicleTypeField delivery={delivery} />,
          width: '100px',
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_addons_happypath',
          ),
          key: 'addOns',
          render: (_, delivery) => <AddOnsField delivery={delivery} />,
          hidden: !haveAnyAddOns,
          width: '100px',
        },
        {
          title: I18n.t(
            'v2.anytimeBooking.deliveryForm.insuranceConfiguration',
          ),
          key: 'deliveryGuarantee',
          render: (_, delivery) => (
            <DeliveryGuaranteeField delivery={delivery} />
          ),
          hidden: !enableInsuranceFee,
          width: '100px',
        },
        {
          title: I18n.t(
            'phrase.geWebBulkUpload.express_web_bulk_upload_list_price_happypath',
          ),
          key: 'price',
          render: (_, delivery) => <PriceField delivery={delivery} />,
          // width: '110px',
        },
      ].filter(c => !c.hidden),
    [deliveries, onToggleExpandDelivery, haveAnyAddOns, enableInsuranceFee],
  );

  // when closing bulk edit sidesheet, clear selection
  const overlayVisible = _get(overlay, 'visible', false);
  const overlayDeliveryIDs = _get(overlay, 'deliveryIDs', []);
  const previousOverlayVisible = usePrevious(overlayVisible);
  useEffect(
    () => {
      if (!overlayVisible && previousOverlayVisible) {
        if (overlayDeliveryIDs.length > 1) {
          onSelectDeliveryIDs([]);
        }
      }
    },
    [overlayDeliveryIDs, overlayVisible, previousOverlayVisible],
  );

  const previousDeliveries = usePrevious(deliveries);
  useEffect(
    () => {
      if (!deliveries || !previousDeliveries) {
        onSelectDeliveryIDs([]);
        return;
      }
      // when deliveries change, clear selection
      if (
        !_isEqual(deliveries.map(d => d.id), previousDeliveries.map(d => d.id))
      ) {
        onSelectDeliveryIDs([]);
      }
    },
    [deliveries, previousDeliveries, onSelectDeliveryIDs],
  );

  const onSelect = useCallback(
    (/* delivery, selected */) => {
      // TODO: for analytics tracking
    },
    [deliveries],
  );

  const onSelectAll = useCallback(
    (/* selected, deliveries */) => {
      if (selectedDeliveryIDs.length === 0) {
        dispatch(
          scribeNewBulkUploadDuck.creators.scribeExpressBukUploadSetupDeliverySelectAll(),
        );
      }
    },
    [dispatch, selectedDeliveryIDs, deliveries],
  );

  const onChange = useCallback(
    deliveryIDs => {
      onSelectDeliveryIDs(
        deliveryIDs.filter(deliveryID => {
          const delivery = deliveries.find(
            delivery => delivery.id === deliveryID,
          );
          const paxDeliveryError = getPaxDeliveryError(delivery);
          // Allow soft block to be selected, only filter out hard blocks
          return (
            delivery &&
            (!paxDeliveryError || isSoftBlockError(paxDeliveryError))
          );
        }),
      );
    },
    [deliveries, onSelectDeliveryIDs],
  );

  const clusterMappings = useSelector(
    bulkUploadGroupingClusterMappingsSelector,
  );

  const groupingIsGrouped = useSelector(bulkUploadGroupingIsGroupedSelector);
  const previousGroupingIsGrouped = usePrevious(groupingIsGrouped);

  const [blinkDeliveryIDs, setBlinkDeliveryIDs] = useState([]);

  useEffect(
    () => {
      if (!!groupingIsGrouped !== !!previousGroupingIsGrouped) {
        const deliveryIDs = [];
        clusterMappings.forEach(({ groupDeliveryID, originalDeliveryIDs }) => {
          deliveryIDs.push(groupDeliveryID);
          deliveryIDs.push(...originalDeliveryIDs);
        });
        setBlinkDeliveryIDs(deliveryIDs);
      }
    },
    [clusterMappings, groupingIsGrouped, previousGroupingIsGrouped],
  );

  const rowClassNameFunc = useCallback(
    delivery => {
      const paxDeliveryError = getPaxDeliveryError(delivery);
      // Only disable row for hard block errors (not soft blocks)
      if (paxDeliveryError && !isSoftBlockError(paxDeliveryError)) {
        return styles.disabledDelivery;
      }
      if (
        !_isEmpty(blinkDeliveryIDs) &&
        blinkDeliveryIDs.includes(delivery.id)
      ) {
        return styles.blinkDelivery;
      }
      return undefined;
    },
    [blinkDeliveryIDs],
  );

  const groupingLayout = useMemo(
    () => {
      if (!shouldShowGroupingOverlay || !bulkUploadFetchingGroupQuote) {
        return null;
      }
      return (
        <div className={cx(styles.loadingOverlay)}>
          <img
            className={cx(styles.loadingOverlayImg)}
            src={groupingDeliveriesSvg}
            alt=""
          />
          <p className={cx(styles.loadingOverlayText)}>
            {I18n.t('bulkUploadRevamp.step3.grouping.overlayText', {
              count: eligibleGroupingDeliveryIDsCount,
            })}
          </p>
        </div>
      );
    },
    [
      bulkUploadFetchingGroupQuote,
      eligibleGroupingDeliveryIDsCount,
      shouldShowGroupingOverlay,
    ],
  );

  return (
    <div className={styles.wrapper}>
      <BulkActions deliveryIDs={selectedDeliveryIDs} />
      {groupingLayout}
      <Table
        className={styles.deliveriesTable}
        rowSelection={{
          onSelect,
          onSelectAll,
          onChange,
          selectedRowKeys: selectedDeliveryIDs,
          getCheckboxProps: record => {
            const paxDeliveryError = _get(record, 'paxDeliveryError');
            // Only disable checkbox for hard block errors, not soft blocks
            const isHardBlock =
              paxDeliveryError && !isSoftBlockError(paxDeliveryError);
            return {
              disabled: isHardBlock,
            };
          },
        }}
        rowClassName={rowClassNameFunc}
        expandedRowKeys={expandedDeliveryIDs}
        expandedRowRender={ExpandedDeliveryRender}
        expandIcon={() => null}
        rowKey={'id'}
        columns={columns}
        dataSource={deliveries}
        pagination={false}
      />
    </div>
  );
};

BulkBookingEditor.propTypes = {
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  overlay: PropTypes.shape({}).isRequired,
  selectedDeliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectDeliveryIDs: PropTypes.func.isRequired,
  bulkUploadFetchingGroupQuote: PropTypes.bool.isRequired,
  eligibleGroupingDeliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  dispatch: PropTypes.func.isRequired,
  shouldShowGroupingOverlay: PropTypes.bool.isRequired,
};

export default connect(state => ({
  overlay: bulkEditOverlaySelector(state),
  bulkUploadFetchingGroupQuote: bulkUploadGroupingFetchingQuoteSelector(state),
}))(BulkBookingEditor);
